import { Device } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { flatten, uniq, uniqBy } from "lodash";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { chunkArray } from "@/utilities/chunkArray";

const DEFAULT_DEVICE_FIELDS = ["id", "name", "serialNumber"];

/**
 * Returns the devices with the given ids.
 *
 * @param ids - The ids of the devices.
 * @returns - The array of {@link Device}.
 */
export function useDevicesByIds(ids?: string[], fields = DEFAULT_DEVICE_FIELDS) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const uniqueIds = ids ? uniq(ids) : [];

  return useQuery<Device[]>({
    queryKey: ["devices", activeSessionId, uniqueIds, fields],

    // The ids are part of the key, so a caller whose set of ids moves — the map, as it is panned —
    // starts a query with no data of its own and would otherwise show nothing until it returns.
    placeholderData: keepPreviousData,

    queryFn: async ({ signal }) => {
      if (uniqueIds.length === 0) {
        return [];
      }

      // Chunks of at most the cap can never fill a page with devices left behind, and
      // DeviceSearch.deviceIds itself accepts at most 5000 ids per query.
      const idChunks = chunkArray(uniqueIds, RESULTS_LIMIT_DEVICE);
      const devicesArrays = await Promise.all(
        idChunks.map(
          idChunk =>
            geotabClient.call(
              "Get",
              {
                typeName: "Device",
                search: { deviceIds: idChunk },
                propertySelector: { fields, isIncluded: true },
                resultsLimit: RESULTS_LIMIT_DEVICE,
              },
              signal,
            ) as Promise<Device[]>,
        ),
      );
      // Not seeded into useDeviceById's ["device", id] cache: these devices carry only `fields`.
      return uniqBy(flatten(devicesArrays), "id");
    },
  });
}
